/**
 * SCRUM-241 — Structura Picking: vinculación del dispositivo.
 *
 * Destino del QR. Con `?code=` canjea solo y entra al hub, que es el caso feliz:
 * el operario escanea y no escribe nada. El formulario manual queda como salida
 * para cuando la cámara no puede leer el código.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { Loader2, ScanLine } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { setDeviceToken, resolvePickingSession } from "@/lib/picking/session";

export const Route = createFileRoute("/picking/pair")({
  component: PairPage,
  validateSearch: (search: Record<string, unknown>) => ({
    code: typeof search.code === "string" ? search.code : undefined,
  }),
  head: () => ({ meta: [{ title: "Vincular dispositivo — Structura Picking" }] }),
});

function PairPage() {
  const { code } = Route.useSearch();
  const navigate = useNavigate();
  const [manualCode, setManualCode] = useState(code ?? "");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const autoTried = useRef(false);

  const claim = useCallback(
    async (pairCode: string) => {
      const trimmed = pairCode.trim();
      if (!trimmed) return;
      setBusy(true);
      setError(null);
      try {
        const res = await fetch("/api/picking/claim", {
          method: "POST",
          credentials: "omit",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ pairCode: trimmed }),
        });
        const data = (await res.json()) as { deviceToken?: string; error?: string };
        if (!res.ok || !data.deviceToken) {
          setError(data.error ?? "No se pudo vincular el dispositivo");
          return;
        }
        setDeviceToken(data.deviceToken);
        // Canjeamos la sesión acá para no aterrizar en el hub con el guard en rojo.
        const session = await resolvePickingSession({ force: true });
        if (!session) {
          setError("El dispositivo se vinculó pero no se pudo iniciar la sesión.");
          return;
        }
        void navigate({ to: "/picking" });
      } catch {
        setError("Sin conexión. Revisá la red y volvé a intentar.");
      } finally {
        setBusy(false);
      }
    },
    [navigate],
  );

  useEffect(() => {
    if (!code || autoTried.current) return;
    autoTried.current = true;
    void claim(code);
  }, [claim, code]);

  return (
    <div className="flex min-h-dvh flex-col items-center justify-center gap-5 bg-background px-6">
      <ScanLine className="h-10 w-10 text-muted-foreground" />
      <div className="space-y-1.5 text-center">
        <h1 className="text-lg font-semibold text-foreground">Structura Picking</h1>
        <p className="max-w-xs text-sm text-muted-foreground">
          {busy
            ? "Vinculando este dispositivo…"
            : "Ingresá el código que aparece en Mi cuenta del workspace."}
        </p>
      </div>

      {busy ? (
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      ) : (
        <div className="w-full max-w-xs space-y-3">
          <Input
            value={manualCode}
            onChange={(e) => setManualCode(e.target.value.toUpperCase())}
            onKeyDown={(e) => e.key === "Enter" && void claim(manualCode)}
            placeholder="ABC123"
            className="text-center font-mono text-lg tracking-[0.3em]"
            autoCapitalize="characters"
            autoCorrect="off"
            spellCheck={false}
            maxLength={12}
          />
          <Button
            className="w-full"
            onClick={() => void claim(manualCode)}
            disabled={!manualCode.trim()}
          >
            Vincular
          </Button>
          {error ? (
            <p role="alert" className="text-center text-xs text-destructive">
              {error}
            </p>
          ) : null}
        </div>
      )}
    </div>
  );
}
